import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { ProfessionalOnlineStatus } from '@klaro7/shared-types';

import { useSharedSocket } from '@/features/calls/context/CallContext';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { getMyProfessionalProfile } from '../services/professional-profile.service';
import {
  getCachedProfessional,
  MY_PROFESSIONAL_PROFILE_QUERY_KEY,
  patchCachedProfessional,
} from '../lib/profileCache';

/**
 * Current professional's own live online status, for the header avatar dot.
 * Initial value comes from the cached /professionals/me query (shared with the
 * profile page — no extra fetch); every backend transition, including `busy`,
 * arrives via `availability:self`.
 */
export function useMyOnlineStatusState() {
  const { user } = useAuth();
  const isPro = user?.role === 'professional';
  const professionalUserId = isPro ? user.id : null;
  const socket = useSharedSocket();
  const queryClient = useQueryClient();
  const [liveState, setLiveState] = useState<{
    userId: string;
    status: ProfessionalOnlineStatus;
    protectedFetchGeneration: number;
  } | null>(null);
  const { data, isLoading, isError } = useQuery({
    queryKey: MY_PROFESSIONAL_PROFILE_QUERY_KEY,
    queryFn: getMyProfessionalProfile,
    enabled: isPro,
  });
  const fetchGenerationRef = useRef(
    queryClient.getQueryState(MY_PROFESSIONAL_PROFILE_QUERY_KEY)?.fetchStatus === 'fetching'
      ? 1
      : 0
  );

  useEffect(() => {
    setLiveState(null);
  }, [user?.id, user?.role]);

  useEffect(() => {
    if (!professionalUserId) return;

    return queryClient.getQueryCache().subscribe((event) => {
      if (
        event.query.queryKey[0] !== MY_PROFESSIONAL_PROFILE_QUERY_KEY[0] ||
        event.query.queryKey[1] !== MY_PROFESSIONAL_PROFILE_QUERY_KEY[1] ||
        event.type !== 'updated'
      ) {
        return;
      }

      if (event.action.type === 'fetch') {
        fetchGenerationRef.current += 1;
        return;
      }

      if (event.action.type !== 'success' || event.action.manual) return;
      setLiveState((current) => {
        if (
          current?.userId !== professionalUserId ||
          fetchGenerationRef.current <= current.protectedFetchGeneration
        ) {
          return current;
        }
        return null;
      });
    });
  }, [professionalUserId, queryClient]);

  useEffect(() => {
    if (!professionalUserId || !socket) return;
    const handler = (payload: unknown): void => {
      if (!payload || typeof payload !== 'object') return;
      const onlineStatus = (payload as { onlineStatus?: unknown }).onlineStatus;
      if (
        onlineStatus !== 'online' &&
        onlineStatus !== 'busy' &&
        onlineStatus !== 'paused' &&
        onlineStatus !== 'offline'
      ) {
        return;
      }
      // Keep the socket value separately: the profile request may still be in
      // flight and a stale REST response must not overwrite a newer live event.
      setLiveState({
        userId: professionalUserId,
        status: onlineStatus,
        protectedFetchGeneration: fetchGenerationRef.current,
      });
      queryClient.setQueryData(
        MY_PROFESSIONAL_PROFILE_QUERY_KEY,
        (old: unknown) => patchCachedProfessional(old, { onlineStatus })
      );
    };
    const handleConnect = (): void => {
      // Events may have been missed while disconnected. Let the refetch become
      // authoritative instead of keeping a pre-disconnect override forever.
      setLiveState(null);
      void queryClient.invalidateQueries({ queryKey: MY_PROFESSIONAL_PROFILE_QUERY_KEY });
    };
    socket.on('availability:self', handler);
    socket.on('connect', handleConnect);
    return () => {
      socket.off('availability:self', handler);
      socket.off('connect', handleConnect);
    };
  }, [professionalUserId, queryClient, socket]);

  const liveStatus = liveState?.userId === professionalUserId
    ? liveState.status
    : null;

  return {
    status: isPro
      ? (liveStatus ?? (data ? (getCachedProfessional(data)?.onlineStatus ?? 'offline') : null))
      : null,
    isLoading: isPro && isLoading,
    isError: isPro && isError,
  };
}

export function useMyOnlineStatus(): ProfessionalOnlineStatus | null {
  return useMyOnlineStatusState().status;
}
